import React, { useEffect, useState } from "react";
import axios from "axios";
import { LoadingSpinner } from "../Notifications/LoadingSpinner";
import TableComponent from "../Common/componets/TableComponent";
import { useQuery } from "react-query";
import { getAllServiceRequest } from "../../utility/api";
import { useTranslation } from "react-i18next";
import Joi from "joi";
import PaginationComponent from "../Common/componets/PaginationComponent";
import FilterDropdown from "../Common/componets/FilterComponent";
import { debounce } from "../../utility/utils";

import BookingModal from "../BookServices/component/BookingModal";
import BackArrowButton from "../Common/componets/backArrowButton";

const ServiceHistory = () => {
  const { t } = useTranslation();
  const [paginationIndex, setPaginationIndex] = useState({
    index: 1,
    limit: 10,
  });
  const [previous, setPrevious] = React.useState([]);
  const [selectedValues, setSelectedValues] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const [loading, setLoading] = useState(true);

  const [serviceStatus, setServiceStatus] = useState({
    isActive: false,
    data: "",
    _id: "",
  });
  const [serviceRequestData, setServiceRequestData] = useState([]);
  const [isBookModal, setBookModal] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery(
    ["getServiceRequest", paginationIndex.index, selectedValues],
    () =>
      getAllServiceRequest(
        paginationIndex.index,
        paginationIndex.limit,
        selectedValues,
        searchValue
      ),
    {
      refetchOnWindowFocus: true,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );

  useEffect(() => {
    if (data?.data?.serviceHistory) {
      setServiceRequestData(data?.data?.serviceHistory);
    }
  }, [data]);
  useEffect(() => {
    if (searchValue !== null) {
      const debouncedSetLoading = debounce(() => refetch(), 800);
      debouncedSetLoading();
    }
  }, [searchValue, refetch]);

  useEffect(() => {
    const serviceHistory = data?.data?.serviceHistory;
    if (serviceHistory) {
      const processedData = serviceHistory?.map((item, index) => ({
        srNo: index + 1,
        carName: `${item.car.brand} ${item.car.model}`,
        userName: `${item.user?.firstName} ${item.user?.lastName}`,
        bookingType: item.bookingType,
        status: item.status,
        date: item.requestedAt.date,
      }));
      setPrevious(processedData);
    }
  }, [data]);

  useEffect(() => {
    if (!isLoading && loading) {
      const debouncedSetLoading = debounce(() => setLoading(false), 500);
      debouncedSetLoading();
    }
  }, [isLoading]);

  // console.log(data);
  if (isLoading && loading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="text-center py-4 text-red-500">Error loading data</div>
    );

  const defaultColumns = [
    {
      accessorKey: "srNo",
      header: t("bookServices.serviceRequestTable.columns.srNo"),
      cell: (info) => info.row.index + 1,
    },
    {
      accessorKey: "carName",
      header: t("bookServices.serviceRequestTable.columns.car"),
      cell: (info) => `${info.row.original?.carName ?? "N/A"}`,
    },
    {
      accessorKey: "userName",
      header: t("bookServices.serviceRequestTable.columns.name"),
      cell: (info) => `${info.row.original?.userName || "N/A"}`,
    },
    {
      accessorKey: "bookingType",
      header: t("bookServices.serviceRequestTable.columns.bookingType"),
      cell: (info) => t(`services.${info.row.original?.bookingType}`) || "N/A",
    },
    {
      accessorKey: "status",
      header: t("bookServices.serviceRequestTable.columns.status"),
      cell: (info) =>
        t(
          `bookServices.serviceRequestTable.status.${info.row.original?.status}`
        ),
    },
    {
      accessorKey: "date",
      header: t("bookServices.serviceRequestTable.columns.bookingDate"),
      cell: (info) =>
        info.row.original?.date
          ? new Date(info.row.original.date).toLocaleDateString()
          : "N/A",
    },
    {
      accessorKey: "_id",
      header: t("services.action"),
      cell: (info) => (
        <p
          className="cursor-pointer text-blue-500"
          onClick={() => {
            setServiceStatus((state) => ({
              ...state,
              _id: info.row.original._id,
            }));
            setBookModal(true);
          }}
        >
          {t("viewImage")}
        </p>
      ),
    },
  ];

  const schema = Joi.object({
    serviceHistory: Joi.array()
      .items(
        Joi.object({
          _id: Joi.string().required(),
          car: Joi.object({
            brand: Joi.string().required(),
            model: Joi.string().required(),
          })
            .unknown(true)
            .required(),
          user: Joi.object({
            firstName: Joi.string().required(),
            lastName: Joi.string().required(),
          })
            .unknown(true)
            .allow(null),
          bookingType: Joi.string().required(),
          status: Joi.string().required(),
          requestedAt: Joi.object({
            date: Joi.string().isoDate().required(),
          })
            .unknown(true)
            .required(),
        }).unknown(true)
      )
      .required(),
    totalRecords: Joi.number().integer().required(),
    limit: Joi.number().integer().required(),
    page: Joi.number().integer().required(),
  }).unknown(true);

  const { serviceHistory, totalRecords, limit, page } = (() => {
    const validationResult = schema.validate(data?.data ?? {}, {
      abortEarly: false,
    });
    if (validationResult.error && !loading) {
      console.error("Validation Error:", validationResult.error.details);
      return {
        serviceHistory: [],
        totalRecords: 0,
        limit: 0,
        page: 0,
        errorMessage: "Data validation failed. Please try again later.",
      };
    }
    return validationResult.value;
  })();

  if (data?.errorMessage) {
    return (
      <div className="text-center py-4 text-red-500">{data.errorMessage}</div>
    );
  }

  const processedData =
    serviceHistory?.map((item, index) => ({
      srNo: index + 1,
      _id: item._id,
      carName: `${item.car.brand} ${item.car.model}`,
      userName: `${item.user?.firstName} ${item.user?.lastName}`,
      bookingType: item.bookingType,
      status: item.status,
      date: item.requestedAt.date,
    })) || previous;

  const filteredOptions = ["pending", "accepted", "rejected", "completed"].map(
    (status) => ({
      label: t(`usedCars.table.${status}`),
      value: status,
    })
  );

  return (
    <div>
      <BookingModal
        serviceStatus={serviceStatus}
        setServiceStatus={setServiceStatus}
        isBookModal={isBookModal}
        setBookModal={setBookModal}
        currentStatus={0}
        data={serviceRequestData}
        setData={setServiceRequestData}
      />
      <div className="min-h-screen px-10 mb-20">
        {isLoading ? <LoadingSpinner /> : null}

        <div className="flex items-end">
          <BackArrowButton />
          <h1 className="text-2xl font-bold text-center py-4 m-auto">
            {t("home.serviceHistory")}
          </h1>
        </div>
        <FilterDropdown
          filteredOptions={filteredOptions}
          setSelectedValues={setSelectedValues}
          selectedValues={
            selectedValues
              ? t(`bookServices.serviceRequestTable.status.${selectedValues}`)
              : ""
          }
          setSearchValue={setSearchValue}
          searchValue={searchValue}
        />
        <TableComponent
          columns={defaultColumns}
          data={processedData?.length > 0 ? processedData : previous}
          paginationIndex={paginationIndex}
          setPaginationIndex={setPaginationIndex}
          totalCount={totalRecords}
          pageSize={10}
        />
        <PaginationComponent
          pageIndex={paginationIndex.index}
          pageCount={Math.ceil(totalRecords / 10)}
          onPageChange={(page) =>
            setPaginationIndex((prev) => ({ ...prev, index: page }))
          }
        />
      </div>
    </div>
  );
};

export default ServiceHistory;
